(function () {
  "use strict";

  if (window.BotaniqueAutoCart) return;

  var FLUSH_DELAY = 25;
  var queue = [];
  var flushTimer = null;
  var flushPromise = null;

  function resolveCartApiUrl(kind) {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (root) {
      return root + {
        add: "cart/add.js",
        change: "cart/change.js",
        cart: "cart.js",
      }[kind];
    }

    var routes = window.Theme && window.Theme.routes;
    if (routes) {
      if (kind === "add") return withJsonSuffix(routes.cart_add_url || "/cart/add");
      if (kind === "change") return withJsonSuffix(routes.cart_change_url || "/cart/change");
      if (kind === "cart") return (routes.cart_url || "/cart") + ".js";
    }

    return {
      add: "/cart/add.js",
      change: "/cart/change.js",
      cart: "/cart.js",
    }[kind];
  }

  function withJsonSuffix(url) {
    return String(url).endsWith(".js") ? url : String(url) + ".js";
  }

  function cartJsonHeaders() {
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
  }

  function getCartSectionIds() {
    var ids = [];
    document.querySelectorAll("cart-items-component").forEach(function (el) {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
    });
    return Array.from(new Set(ids)).join(",");
  }

  function normalizeAddItem(item) {
    if (!item || item.id == null) return null;
    var next = {
      id: parseInt(item.id, 10),
      quantity: Number(item.quantity || 1),
    };
    if (item.selling_plan != null) next.selling_plan = parseInt(item.selling_plan, 10);
    if (item.properties) next.properties = item.properties;
    return next;
  }

  function addItemKey(item) {
    return [
      item.id,
      item.selling_plan || "",
      JSON.stringify(item.properties || {}),
    ].join("|");
  }

  function combineAddItems(items) {
    var byKey = new Map();
    items.map(normalizeAddItem).filter(Boolean).forEach(function (item) {
      var key = addItemKey(item);
      var existing = byKey.get(key);
      if (existing) {
        existing.quantity += item.quantity;
      } else {
        byKey.set(key, item);
      }
    });
    return Array.from(byKey.values());
  }

  function normalizeChange(change) {
    if (!change || change.quantity == null) return null;
    var next = { quantity: Number(change.quantity) };
    if (change.id != null) next.id = String(change.id);
    if (change.line != null) next.line = Number(change.line);
    if (!next.id && !next.line) return null;
    return next;
  }

  function combineChanges(changes) {
    var keyed = new Map();
    var lineChanges = [];

    changes.map(normalizeChange).filter(Boolean).forEach(function (change) {
      if (change.id) {
        keyed.set(change.id, change);
      } else {
        lineChanges.push(change);
      }
    });

    lineChanges.sort(function (a, b) {
      return (b.line || 0) - (a.line || 0);
    });

    return Array.from(keyed.values()).concat(lineChanges);
  }

  async function parseCartResponse(response) {
    var data = await response.json();
    if (!response.ok || data.errors || data.message || data.status) {
      throw new Error(data.description || data.message || data.errors || "Cart mutation failed");
    }
    return data;
  }

  async function postCartMutation(url, payload) {
    var response = await fetch(url, {
      method: "POST",
      headers: cartJsonHeaders(),
      body: JSON.stringify(payload),
    });
    return parseCartResponse(response);
  }

  function isLineKey(value) {
    return typeof value === "string" && value.indexOf(":") > -1;
  }

  async function hasCartLine(change) {
    if (!change || !change.id || !isLineKey(change.id)) return true;

    var cart = window.BotaniqueCartCache && typeof window.BotaniqueCartCache.getCart === "function"
      ? await window.BotaniqueCartCache.getCart({ force: true })
      : await fetch(resolveCartApiUrl("cart"), { headers: { Accept: "application/json" } }).then(function (response) {
          return response.json();
        });

    return (cart.items || []).some(function (item) {
      return String(item.key) === String(change.id);
    });
  }

  async function shouldSkipChange(change) {
    if (!change || change.quantity !== 0 || !change.id) return false;
    return !(await hasCartLine(change));
  }

  function dispatchQueueEvent(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail }));
  }

  function dispatchCartUpdate(data, source) {
    if (!data) return;
    document.dispatchEvent(
      new CustomEvent("cart:update", {
        detail: {
          data: {
            itemCount: data.item_count,
            sections: data.sections,
            source: source || "auto-cart-mutations",
          },
        },
      }),
    );
  }

  function shouldShowGiftLoaderForEntry(entry) {
    if (!entry || !entry.addItems || entry.addItems.length === 0) return false;
    return !/upsell/i.test(entry.source || "");
  }

  async function refreshKachingCart() {
    if (window.kachingCartApi && typeof window.kachingCartApi.refreshCart === "function") {
      await window.kachingCartApi.refreshCart();
    }
  }

  function setAutoCartMutationActive(isActive) {
    window.__bpAutoCartMutationDepth = Math.max(
      0,
      (window.__bpAutoCartMutationDepth || 0) + (isActive ? 1 : -1),
    );
  }

  async function flush() {
    window.clearTimeout(flushTimer);
    flushTimer = null;
    if (flushPromise) return flushPromise;

    flushPromise = (async function () {
      var batch = queue;
      queue = [];

      var addItems = combineAddItems(batch.flatMap(function (entry) { return entry.addItems || []; }));
      var changes = combineChanges(batch.flatMap(function (entry) { return entry.changes || []; }));
      var resolvers = batch.flatMap(function (entry) { return entry.resolvers || []; });
      var hasAddedItems = addItems.length > 0;
      var hasGiftAddedItems = batch.some(shouldShowGiftLoaderForEntry);
      var source = batch.map(function (entry) { return entry.source; }).filter(Boolean).join("+") || "auto-cart-mutations";
      var sections = getCartSectionIds();
      var lastData = null;

      if (!hasAddedItems && changes.length === 0) {
        resolvers.forEach(function (entry) { entry.resolve(null); });
        return null;
      }

      if (hasGiftAddedItems) document.body.classList.add("bp-auto-gift-add-loading");
      dispatchQueueEvent("botanique:auto-cart:start", {
        hasAddedItems: hasAddedItems,
        hasGiftAddedItems: hasGiftAddedItems,
        source: source,
      });

      try {
        setAutoCartMutationActive(true);
        if (hasAddedItems) {
          var addPayload = {
            items: addItems,
            sections: sections,
            sections_url: window.location.pathname,
          };
          lastData = await postCartMutation(resolveCartApiUrl("add"), addPayload);
        }

        for (var i = 0; i < changes.length; i += 1) {
          var change = changes[i];
          if (await shouldSkipChange(change)) continue;

          var changePayload = {
            quantity: change.quantity,
            sections: sections,
            sections_url: window.location.pathname,
          };
          if (change.id) changePayload.id = change.id;
          else changePayload.line = change.line;
          lastData = await postCartMutation(resolveCartApiUrl("change"), changePayload);
        }

        dispatchCartUpdate(lastData, source);
        await refreshKachingCart();
        resolvers.forEach(function (entry) { entry.resolve(lastData); });
        return lastData;
      } catch (error) {
        resolvers.forEach(function (entry) { entry.reject(error); });
        throw error;
      } finally {
        setAutoCartMutationActive(false);
        if (hasGiftAddedItems) document.body.classList.remove("bp-auto-gift-add-loading");
        dispatchQueueEvent("botanique:auto-cart:finish", {
          hasAddedItems: hasAddedItems,
          hasGiftAddedItems: hasGiftAddedItems,
          source: source,
        });
        flushPromise = null;
        if (queue.length > 0) scheduleFlush();
      }
    })();

    return flushPromise;
  }

  function scheduleFlush() {
    if (!flushTimer) flushTimer = window.setTimeout(flush, FLUSH_DELAY);
  }

  window.BotaniqueAutoCart = {
    enqueue: function (request) {
      return new Promise(function (resolve, reject) {
        queue.push({
          source: request && request.source,
          addItems: request && request.addItems ? request.addItems : [],
          changes: request && request.changes ? request.changes : [],
          resolvers: [{ resolve: resolve, reject: reject }],
        });
        scheduleFlush();
      });
    },
    flush: flush,
  };
})();
